import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import {
  FaMoneyBill, FaUserFriends, FaCalendarCheck, FaPlus, FaChartBar,
  FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight,
  FaSignOutAlt, FaTimes, FaFilePdf,
  FaTrashAlt, FaPen, FaFileInvoiceDollar, FaEquals, FaSearch,
  FaArrowLeft, FaArrowRight,
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import './css/AdminPage.css';
import useAuthRedirect from '../hooks/useAuthRedirect';
import logo from '../assets/logo.png';
import { toastSuccess, toastError } from '../utils/toast';
import { money, aNumero, limpiarNumero } from '../utils/format';
import Money from './ui/Money';
import RankingMozos from './ui/RankingMozos';
import GraficoMeses from './ui/GraficoMeses';
import BarrasDiaSemana from './ui/BarrasDiaSemana';
import { getPresetRange, startOfDayMVD, endOfDayMVD, todayYmdMVD } from '../utils/dateRanges';
import Spinner, { LoadingScreen } from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import DateRangePresets from './ui/DateRangePresets';
import GenerarCierrePage from './GenerarCierrePage';

Modal.setAppElement('#root');

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

ChartJS.defaults.color = '#c7c7cf';
ChartJS.defaults.borderColor = 'rgba(38, 38, 46, 0.8)';

// Parseo seguro de historialcierres.detalles (TEXT con JSON). Devuelve [] si es
// NULL/corrupto, para no tirar la pantalla con un JSON.parse sin proteger.
const parseDetalles = (raw) => {
  try {
    const arr = JSON.parse(raw || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

// Modos de reparto del pozo del día (deben coincidir con los del backend).
const MODO_PORCENTAJE = 'porcentaje';
const MODO_PUNTOS = 'puntos';

// Monto que recibió un empleado en un cierre.
//
// Los cierres nuevos guardan el monto EXACTO que acreditó el backend, así que se
// usa tal cual. La fórmula sólo se usa como fallback para los cierres viejos,
// guardados antes de que el backend incluyera `monto` en el JSON (y por eso no
// contempla el reparto por puntos: en esa época no existía).
const computePayout = (detail, totalPropina, allDetails) => {
  const guardado = Number(detail?.monto);
  if (detail?.monto !== undefined && detail?.monto !== null && Number.isFinite(guardado)) {
    return guardado;
  }
  const sumSpecific = allDetails.reduce((s, d) => s + (Number(d.specificAmount) || 0), 0);
  const remaining = Math.max(0, Number(totalPropina) - sumSpecific);
  const specific = Number(detail.specificAmount) || 0;
  const pct = Number(detail.porcentaje) || 0;
  return specific + (pct / 100) * remaining;
};

// Reparte un monto entero según pesos relativos (puntos) con el método del resto
// mayor. Es la MISMA aritmética que `repartirPorPesos` del backend
// (src/models/restaurantModel.js): se replica acá sólo para la vista previa, así
// el restaurante ve exactamente los pesos que se van a acreditar antes de
// cerrar. La fuente de verdad sigue siendo el backend.
const repartirPorPesos = (base, pesos) => {
  const totalPeso = pesos.reduce((s, x) => s + x, 0);
  if (totalPeso <= 0 || base <= 0) return pesos.map(() => 0);

  const cuotas = pesos.map((peso) => (base * peso) / totalPeso);
  const montos = cuotas.map((c) => Math.floor(c));
  let sobrante = base - montos.reduce((s, n) => s + n, 0);

  const orden = cuotas
    .map((c, i) => ({ i, frac: c - Math.floor(c) }))
    .sort((a, b) => (b.frac - a.frac) || (pesos[b.i] - pesos[a.i]) || (a.i - b.i));

  let k = 0;
  while (sobrante > 0 && orden.length > 0) {
    montos[orden[k % orden.length].i] += 1;
    sobrante -= 1;
    k += 1;
  }
  return montos;
};

// Los puntos se muestran sin decimales cuando son enteros (1 en vez de 1.00).
const fmtPuntos = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : String(v).replace('.', ',');
};

function AdminPage() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedPage, setSelectedPage] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('si_sidebarCollapsed');
      return stored === null ? true : stored === '1';
    } catch {
      return true;
    }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('si_sidebarCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  // Modo de reparto del pozo. Se guarda en el backend (no en el navegador) para
  // que la preferencia siga al restaurante entre la web y la app.
  const [modoReparto, setModoReparto] = useState(MODO_PORCENTAJE);
  const [history, setHistory] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    Ci: '',
    Nombre: '',
    Apellido: '',
    email: '',
    telefono: '',
    password: '',
    nCuenta: '',
    banco: '',
    tipoCuenta: '',
    puntos: 1,
  });
  const [tipAmount, setTipAmount] = useState('');
  const [dailyTips, setDailyTips] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [confirmLiquidar, setConfirmLiquidar] = useState({ open: false, employee: null });
  const [isLiquidando, setIsLiquidando] = useState(false);
  const [verBajas, setVerBajas] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditTipModalOpen, setIsEditTipModalOpen] = useState(false);
  const [newTipAmount, setNewTipAmount] = useState(dailyTips);
  const [openCards, setOpenCards] = useState({});
  const [startDate, setStartDate] = useState(() => getPresetRange('month')?.start || '');
  const [endDate, setEndDate] = useState(() => getPresetRange('month')?.end || '');
  const [historyPreset, setHistoryPreset] = useState('month');
  const [statsPreset, setStatsPreset] = useState('custom');
  const [payoutReport, setPayoutReport] = useState(null);
  const [payoutStart, setPayoutStart] = useState('');
  const [payoutEnd, setPayoutEnd] = useState('');
  const [payoutPreset, setPayoutPreset] = useState('month');
  const [payoutOpen, setPayoutOpen] = useState({});

  // Loading states
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [loadingDailyTips, setLoadingDailyTips] = useState(false);
  const [isAddingTip, setIsAddingTip] = useState(false);
  const [isUpdatingTip, setIsUpdatingTip] = useState(false);
  const [isClosingDay, setIsClosingDay] = useState(false);
  const [isSubmittingEmployee, setIsSubmittingEmployee] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(false);

  // Confirm dialog para eliminar empleado
  const [confirmDelete, setConfirmDelete] = useState({ open: false, employee: null });

  // Búsquedas y paginación
  const [employeeListSearch, setEmployeeListSearch] = useState('');
  const [closeDaySearch, setCloseDaySearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 20;

  useAuthRedirect();

  useEffect(() => {
    if (selectedPage === 'dashboard') {
      fetchDailyTips();
      fetchEmployees();
      fetchHistory();
      fetchConfig();
    }
    if (selectedPage === 'users') {
      fetchEmployees();
    }
    if (selectedPage === 'history') {
      fetchHistory();
    }
    if (selectedPage === 'stats') {
      fetchHistory();
    }
    if (selectedPage === 'payouts') {
      fetchPayoutReport({ period: 'mensual' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage]);

  const toggleCard = (id) => {
    setOpenCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchPayoutReport = async (opts = {}) => {
    setLoadingPayouts(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();

      if (opts.period) {
        params.append('period', opts.period);
      } else {
        if (payoutStart) params.append('start', payoutStart);
        if (payoutEnd) params.append('end', payoutEnd);
      }

      const res = await axios.get(
        `${apiUrl}/restaurante/reporte-pagos?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayoutReport(res.data);
    } catch (err) {
      console.error('Error al traer reporte:', err);
      toastError('No pudimos cargar el reporte de pagos.');
    } finally {
      setLoadingPayouts(false);
    }
  };

  const downloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();

      if (payoutStart) params.append('start', payoutStart);
      if (payoutEnd) params.append('end', payoutEnd);
      else params.append('period', 'mensual');

      params.append('format', 'pdf');

      const res = await axios.get(
        `${apiUrl}/restaurante/reporte-pagos?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reporte_pagos.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess('PDF descargado.');
    } catch (err) {
      console.error('Error al descargar PDF:', err);
      toastError('No pudimos generar el PDF.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const token = localStorage.getItem('token');
      // Se piden TAMBIEN los dados de baja: son los unicos que pueden tener
      // saldo sin cobrar y no se los veia en ningun lado. Donde no
      // corresponden (el cierre del dia, el total acumulado) se filtran abajo.
      const response = await axios.get(`${apiUrl}/empleados?incluirInactivos=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.empleados);
    } catch (error) {
      console.error('Error al obtener la lista de empleados:', error);
      toastError('No pudimos cargar la lista de empleados.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/restaurante/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.modoReparto) setModoReparto(response.data.modoReparto);
    } catch (error) {
      // Si el backend todavía no tiene el endpoint (deploy a mitad de camino),
      // seguimos en porcentaje, que es el comportamiento de siempre.
      console.error('No pudimos leer la configuración del restaurante:', error);
    }
  };

  const fetchDailyTips = async () => {
    setLoadingDailyTips(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/restaurante/propinasHoy`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyTips(response.data.propinasHoy);
    } catch (error) {
      console.error('Error al obtener las propinas del día:', error);
      toastError('No pudimos cargar las propinas del día.');
    } finally {
      setLoadingDailyTips(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/restaurante/historial`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.historial);
    } catch (error) {
      console.error('Error al obtener el historial:', error);
      toastError('No pudimos cargar el historial.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee({ ...newEmployee, [name]: value });
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (isSubmittingEmployee) return;
    setIsSubmittingEmployee(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${apiUrl}/usuario`, { ...newEmployee, puntos: aNumero(newEmployee.puntos) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmployees();
      setNewEmployee({ id: '', Ci: '', Nombre: '', Apellido: '', email: '', telefono: '', password: '', nCuenta: '', banco: '', tipoCuenta: '', puntos: 1 });
      setIsModalOpen(false);
      toastSuccess('Mozo agregado correctamente.');
    } catch (error) {
      console.error('Error al agregar el empleado:', error);
      toastError('No pudimos agregar el mozo: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSubmittingEmployee(false);
    }
  };

  const handleAddTip = async () => {
    if (isAddingTip) return;
    if (isNaN(tipAmount) || tipAmount <= 0) {
      toastError('Ingresá una cantidad válida de propina.');
      return;
    }
    setIsAddingTip(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/restaurante/propinas`,
        { propina: parseFloat(tipAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDailyTips(dailyTips + parseFloat(tipAmount));
      setTipAmount('');
      toastSuccess(`Propina de ${money(parseFloat(tipAmount))} agregada.`);
    } catch (error) {
      console.error('Error al agregar la propina:', error);
      toastError('No pudimos agregar la propina.');
    } finally {
      setIsAddingTip(false);
    }
  };

  const handleSelectEmployee = (employeeId, percentage) => {
    const updatedSelection = selectedEmployees.map(emp =>
      emp.id === employeeId ? { ...emp, percentage } : emp
    );
    setSelectedEmployees(updatedSelection);
  };

  // Puntos configurados del empleado (los de su ficha). Es el valor con el que
  // arranca cada cierre; se puede pisar a mano para ese día puntual.
  const puntosDeEmpleado = (employeeId) => {
    const emp = employees.find((e) => e.id === employeeId);
    const p = Number(emp?.puntos);
    return Number.isFinite(p) ? p : 1;
  };

  const handleToggleEmployee = (employeeId) => {
    const alreadySelected = selectedEmployees.find(emp => emp.id === employeeId);
    if (alreadySelected) {
      setSelectedEmployees(selectedEmployees.filter(emp => emp.id !== employeeId));
    } else {
      setSelectedEmployees([
        ...selectedEmployees,
        { id: employeeId, percentage: 0, specificAmount: 0, puntos: String(puntosDeEmpleado(employeeId)).replace('.', ',') },
      ]);
    }
  };

  const handleSetSpecificAmount = (employeeId, amount) => {
    const updatedSelection = selectedEmployees.map(emp =>
      emp.id === employeeId ? { ...emp, specificAmount: parseFloat(amount) || 0 } : emp
    );
    setSelectedEmployees(updatedSelection);
  };

  // Guarda lo que la persona escribió, no el número: si se normalizara acá,
  // la coma de "0,3" se borraría en el mismo momento de tipearla.
  const handleSetPuntos = (employeeId, puntos) => {
    const updatedSelection = selectedEmployees.map(emp =>
      emp.id === employeeId ? { ...emp, puntos: limpiarNumero(puntos) } : emp
    );
    setSelectedEmployees(updatedSelection);
  };

  const handleChangeModo = async (modo) => {
    if (modo === modoReparto) return;
    setModoReparto(modo);

    // Al pasar a puntos, precargamos los puntos de ficha de los que ya estaban
    // tildados: si no, quedarían todos en 0 y no se podría cerrar.
    if (modo === MODO_PUNTOS) {
      setSelectedEmployees((prev) => prev.map((sel) => ({
        ...sel,
        puntos: sel.puntos === undefined || sel.puntos === null || sel.puntos === ''
          ? String(puntosDeEmpleado(sel.id)).replace('.', ',')
          : sel.puntos,
      })));
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${apiUrl}/restaurante/config`,
        { modoReparto: modo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Que no se pueda guardar la preferencia no debe impedir cerrar el día:
      // el modo elegido igual viaja en el request de cierre.
      console.error('No pudimos guardar el modo de reparto:', error);
    }
  };

  const handleCloseDay = async () => {
    if (isClosingDay) return;

    const totalFijos = selectedEmployees.reduce((acc, emp) => acc + (Number(emp.specificAmount) || 0), 0);
    if (totalFijos > dailyTips) {
      toastError(`Los montos fijos ($${totalFijos}) superan el total del día ($${dailyTips}).`);
      return;
    }

    if (modoReparto === MODO_PUNTOS) {
      // En puntos no hay un 100% que validar: alcanza con que haya puntos
      // cargados y que nadie quede tildado sin puntos ni monto fijo (no
      // cobraría nada y sería un error silencioso).
      const enCero = selectedEmployees.filter(
        (emp) => aNumero(emp.puntos) <= 0 && (Number(emp.specificAmount) || 0) <= 0
      );
      if (enCero.length > 0) {
        toastError(`Hay ${enCero.length} mozo(s) seleccionados sin puntos ni monto fijo.`);
        return;
      }
      if (totalPuntosSelected <= 0) {
        toastError('Asigná puntos a por lo menos un mozo.');
        return;
      }
    } else {
      const totalPercentage = selectedEmployees.reduce((acc, emp) => acc + emp.percentage, 0);
      if (totalPercentage !== 100) {
        toastError('El porcentaje total debe ser exactamente 100%.');
        return;
      }
    }

    setIsClosingDay(true);
    try {
      const token = localStorage.getItem('token');
      // Los puntos viajan como número: en el estado son texto ("0,3") porque
      // es lo que se escribió, pero el backend espera un number.
      const payload = selectedEmployees.map((emp) => ({
        ...emp,
        puntos: aNumero(emp.puntos),
      }));
      await axios.post(
        `${apiUrl}/restaurante/cerrarDia`,
        { selectedEmployees: payload, modo: modoReparto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Día cerrado y propinas distribuidas.');
      fetchDailyTips();
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Error al cerrar el día:', error);
      toastError('No pudimos cerrar el día: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsClosingDay(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const requestDeleteEmployee = (employee) => {
    setConfirmDelete({ open: true, employee });
  };

  const performDeleteEmployee = async () => {
    const employee = confirmDelete.employee;
    if (!employee) return;
    setIsDeletingEmployee(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/empleado/${employee.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Mozo desactivado correctamente.');
      setConfirmDelete({ open: false, employee: null });
      fetchEmployees();
    } catch (error) {
      console.error('Error al desactivar el empleado:', error);
      toastError('No pudimos desactivar al mozo: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsDeletingEmployee(false);
    }
  };

  const handleUpdateEmployee = async (employee) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.patch(
        `${apiUrl}/empleado`,
        {
          id: employee.id,
          email: employee.email,
          telefono: employee.telefono,
          nCuenta: employee.nCuenta,
          banco: employee.banco,
          tipoCuenta: employee.tipoCuenta,
          puntos: aNumero(employee.puntos),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess(response.data.message || 'Empleado actualizado.');
      fetchEmployees();
    } catch (error) {
      console.error('Error al actualizar el empleado:', error);
      toastError('No pudimos actualizar el mozo: ' + (error.response?.data?.error || error.message));
      throw error;
    }
  };

  const handleReactivarEmployee = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${apiUrl}/empleado/${employee.id}/reactivar`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastSuccess(`${employee.Nombre} vuelve a estar activo.`);
      fetchEmployees();
    } catch (error) {
      toastError('No pudimos reactivar al mozo: ' + (error.response?.data?.error || error.message));
    }
  };

  const performLiquidar = async () => {
    const employee = confirmLiquidar.employee;
    if (!employee) return;
    setIsLiquidando(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${apiUrl}/empleado/${employee.id}/liquidar`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastSuccess(`Liquidado ${money(data.monto)} a ${employee.Nombre}.`);
      setConfirmLiquidar({ open: false, employee: null });
      fetchEmployees();
    } catch (error) {
      toastError('No pudimos liquidar el saldo: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLiquidando(false);
    }
  };

  const openEditModal = (employee) => {
    setEditEmployee(employee);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditEmployee({ ...editEmployee, [name]: value });
  };

  const handleSaveEdit = async () => {
    if (!editEmployee || isSavingEdit) return;
    setIsSavingEdit(true);
    try {
      await handleUpdateEmployee(editEmployee);
      setIsEditModalOpen(false);
    } catch {
      // toast ya mostrado en handleUpdateEmployee
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openEditTipModal = () => {
    setNewTipAmount(dailyTips);
    setIsEditTipModalOpen(true);
  };
  const closeEditTipModal = () => setIsEditTipModalOpen(false);

  const handleUpdateTip = async () => {
    if (isUpdatingTip) return;
    if (isNaN(newTipAmount) || newTipAmount < 0) {
      toastError('Ingresá una cantidad válida de propina.');
      return;
    }
    setIsUpdatingTip(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${apiUrl}/restaurante/propinasHoy`,
        { propina: parseFloat(newTipAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDailyTips(parseFloat(newTipAmount));
      closeEditTipModal();
      toastSuccess('Propina del día actualizada.');
    } catch (error) {
      console.error('Error al actualizar la propina del día:', error);
      toastError('No pudimos actualizar la propina del día.');
    } finally {
      setIsUpdatingTip(false);
    }
  };

  const distributeEqually = () => {
    if (selectedEmployees.length === 0) {
      toastError('Seleccioná al menos un mozo.');
      return;
    }

    // En puntos, "igual para todos" es simplemente 1 punto cada uno: el reparto
    // proporcional se encarga del resto y no hay redondeos que cuadrar a mano.
    if (modoReparto === MODO_PUNTOS) {
      setSelectedEmployees(selectedEmployees.map((emp) => ({ ...emp, puntos: '1', specificAmount: 0 })));
      return;
    }

    const equalPercentage = Math.floor(100 / selectedEmployees.length);
    const updatedSelection = selectedEmployees.map((emp, index) => ({
      ...emp,
      percentage: index === selectedEmployees.length - 1
        ? 100 - (equalPercentage * (selectedEmployees.length - 1))
        : equalPercentage,
    }));

    setSelectedEmployees(updatedSelection);
  };

  const generateChartData = (dataHistory) => {
    const sorted = [...dataHistory].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    const labels = sorted.map(e => new Date(e.fecha).toLocaleDateString('es-ES', { timeZone: 'America/Montevideo' }));
    const data = sorted.map(e => e.propina);

    return {
      labels,
      datasets: [{
        label: 'Propinas Diarias',
        data,
        borderColor: '#b28cf5',
        backgroundColor: 'rgba(178, 140, 245, 0.18)',
        pointBackgroundColor: '#b28cf5',
        pointBorderColor: '#0c0c0f',
        pointBorderWidth: 2,
        pointRadius: 3,
        // Poco suavizado a propósito: son días sueltos, no una medición
        // continua. Una curva muy redondeada inventa valores entre día y día.
        tension: 0.15,
        fill: true,
      }]
    };
  };

  /**
   * Cuánto le tocó a cada mozo en el rango elegido, ordenado de mayor a menor.
   * Antes esto era una torta: se cambió por un ranking de barras porque
   * comparar ángulos es lo que peor hace el ojo, y porque su paleta se ciclaba
   * cada 10 colores (con 11 mozos, dos personas compartían color).
   */
  const rankingPorMozo = (dataHistory) => {
    const totales = {};
    dataHistory.forEach(entry => {
      const detalles = parseDetalles(entry.detalles);
      detalles.forEach(detail => {
        const clave = detail.id != null
          ? `id:${detail.id}`
          : `${detail.nombre || ''} ${detail.apellido || ''}`.trim();
        if (!clave) return;
        if (!totales[clave]) {
          totales[clave] = {
            nombre: detail.nombre || '',
            apellido: detail.apellido || '',
            total: 0,
          };
        }
        totales[clave].total += computePayout(detail, entry.propina, detalles);
      });
    });
    return Object.values(totales);
  };

  // Solo empleados en actividad: es el numero que el restaurante entiende como
  // "lo que le debo a mi equipo". Lo de la gente que se fue se muestra aparte.
  const empleadosActivos = employees.filter((e) => e.activo !== false);
  const empleadosDeBaja = employees.filter((e) => e.activo === false);
  const saldoDeBajas = empleadosDeBaja.reduce((t, e) => t + (e.propina || 0), 0);

  const calculateTotalPropinasAcumuladas = () => {
    return empleadosActivos.reduce((total, emp) => total + (emp.propina || 0), 0);
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    if (isSubmittingEmployee) return;
    setIsModalOpen(false);
  };

  const totalPercentageSelected = selectedEmployees.reduce((acc, emp) => acc + (emp.percentage || 0), 0);
  const totalPuntosSelected = selectedEmployees.reduce((acc, emp) => acc + aNumero(emp.puntos), 0);
  const totalFijosSelected = selectedEmployees.reduce((acc, emp) => acc + (Number(emp.specificAmount) || 0), 0);

  // Lo que queda para el reparto proporcional después de los montos fijos.
  const baseResidual = Math.max(0, Number(dailyTips || 0) - totalFijosSelected);
  const valorPunto = totalPuntosSelected > 0 ? baseResidual / totalPuntosSelected : 0;

  // Vista previa: cuánto le toca a cada uno con la carga actual. Replica la
  // aritmética del backend (incluido el redondeo) para que no haya sorpresas
  // entre lo que se ve en pantalla y lo que se acredita.
  const previewMontos = useMemo(() => {
    const out = {};
    const sumaFijos = selectedEmployees.reduce((s, e) => s + (Number(e.specificAmount) || 0), 0);
    const base = Math.max(0, Number(dailyTips || 0) - sumaFijos);
    selectedEmployees.forEach((e) => { out[e.id] = Number(e.specificAmount) || 0; });

    if (modoReparto === MODO_PUNTOS) {
      const conPuntos = selectedEmployees.filter((e) => aNumero(e.puntos) > 0);
      const montos = repartirPorPesos(Math.round(base), conPuntos.map((e) => aNumero(e.puntos)));
      conPuntos.forEach((e, i) => { out[e.id] = (out[e.id] || 0) + montos[i]; });
    } else {
      const conPct = selectedEmployees.filter((e) => (Number(e.percentage) || 0) > 0);
      const sumaPct = conPct.reduce((s, e) => s + Number(e.percentage), 0);
      const cierraEn100 = Math.abs(sumaPct - 100) <= 0.01;
      let repartido = 0;
      conPct.forEach((e, i) => {
        const esUltimo = i === conPct.length - 1;
        // El backend le da el remanente exacto al último, pero sólo tiene
        // sentido mostrarlo así cuando los porcentajes cierran en 100.
        const amount = esUltimo && cierraEn100
          ? base - repartido
          : Math.round(base * (Number(e.percentage) / 100));
        if (!esUltimo) repartido += amount;
        out[e.id] = (out[e.id] || 0) + amount;
      });
    }
    return out;
  }, [selectedEmployees, dailyTips, modoReparto]);

  const puntosValidos =
    totalPuntosSelected > 0 &&
    selectedEmployees.every(
      (emp) => aNumero(emp.puntos) > 0 || (Number(emp.specificAmount) || 0) > 0
    );

  const canCloseDay = selectedEmployees.length > 0 && (
    modoReparto === MODO_PUNTOS ? puntosValidos : totalPercentageSelected === 100
  );

  // Manejo de presets de fecha (history y stats comparten startDate/endDate)
  const applyHistoryPreset = (key) => {
    setHistoryPreset(key);
    if (key === 'custom') return;
    const r = getPresetRange(key);
    if (r) {
      setStartDate(r.start);
      setEndDate(r.end);
    }
  };
  const applyStatsPreset = (key) => {
    setStatsPreset(key);
    if (key === 'custom') return;
    const r = getPresetRange(key);
    if (r) {
      setStartDate(r.start);
      setEndDate(r.end);
    }
  };
  const applyPayoutPreset = (key) => {
    setPayoutPreset(key);
    if (key === 'custom') return;
    if (key === 'month') {
      setPayoutStart('');
      setPayoutEnd('');
      fetchPayoutReport({ period: 'mensual' });
      return;
    }
    if (key === 'lastMonth') {
      const r = getPresetRange('lastMonth');
      setPayoutStart(r.start);
      setPayoutEnd(r.end);
      // Disparamos fetch con esas fechas vía useEffect-like manual:
      setTimeout(() => fetchPayoutReportWith(r.start, r.end), 0);
      return;
    }
    const r = getPresetRange(key);
    if (r) {
      setPayoutStart(r.start);
      setPayoutEnd(r.end);
      setTimeout(() => fetchPayoutReportWith(r.start, r.end), 0);
    }
  };

  const fetchPayoutReportWith = async (start, end) => {
    setLoadingPayouts(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      const res = await axios.get(
        `${apiUrl}/restaurante/reporte-pagos?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayoutReport(res.data);
    } catch (err) {
      console.error('Error al traer reporte:', err);
      toastError('No pudimos cargar el reporte de pagos.');
    } finally {
      setLoadingPayouts(false);
    }
  };

  // ============================================================
  // Widgets del dashboard — derivados puros de `history`
  // ============================================================
  const dashboardWidgets = useMemo(() => {
    const empty = {
      topEmployees: [],
      last7Days: [],
      thisMonth: 0,
      lastMonth: 0,
      deltaPct: null,
      monthLabel: new Date().toLocaleDateString('es-ES', { month: 'long', timeZone: 'America/Montevideo' }),
    };
    if (!history || history.length === 0) return empty;

    const now = new Date();
    const monthRange = getPresetRange('month', now);
    const lastMonthRange = getPresetRange('lastMonth', now);
    const startThisMonth = startOfDayMVD(monthRange.start);
    const endThisMonth = endOfDayMVD(monthRange.end);
    const startLastMonth = startOfDayMVD(lastMonthRange.start);
    const endLastMonth = endOfDayMVD(lastMonthRange.end);

    // parseDetalles y computePayout ahora son helpers de módulo (reutilizados
    // por generatePieChartData), definidos arriba del componente.

    // 1) Top mozos del mes actual
    const totals = {};
    for (const entry of history) {
      const fecha = new Date(entry.fecha);
      if (fecha < startThisMonth || fecha > endThisMonth) continue;
      const detalles = parseDetalles(entry.detalles);
      for (const d of detalles) {
        const key =
          d.id != null
            ? `id:${d.id}`
            : `${d.nombre || ''} ${d.apellido || ''}`.trim();
        if (!key) continue;
        const monto = computePayout(d, entry.propina, detalles);
        if (!totals[key]) {
          totals[key] = {
            nombre: d.nombre || '',
            apellido: d.apellido || '',
            total: 0,
          };
        }
        totals[key].total += monto;
      }
    }
    const topEmployees = Object.values(totals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    // 2) Últimos 7 días (incluyendo hoy) — calculados en zona Montevideo
    const last7Days = [];
    const todayStartMVD = startOfDayMVD(todayYmdMVD(now));
    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(todayStartMVD.getTime() - i * 24 * 60 * 60 * 1000);
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      const total = history
        .filter((e) => {
          const f = new Date(e.fecha);
          return f >= dayStart && f < dayEnd;
        })
        .reduce((s, e) => s + (Number(e.propina) || 0), 0);
      last7Days.push({
        label: dayStart.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', timeZone: 'America/Montevideo' }),
        total,
      });
    }

    // 3) Mes actual vs mes anterior
    const sumInRange = (start, end) =>
      history
        .filter((e) => {
          const f = new Date(e.fecha);
          return f >= start && f <= end;
        })
        .reduce((s, e) => s + (Number(e.propina) || 0), 0);

    const thisMonth = sumInRange(startThisMonth, endThisMonth);
    const lastMonth = sumInRange(startLastMonth, endLastMonth);
    const deltaPct =
      lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : null;

    const monthLabel = startThisMonth.toLocaleDateString('es-ES', {
      month: 'long',
      timeZone: 'America/Montevideo',
    });

    return { topEmployees, last7Days, thisMonth, lastMonth, deltaPct, monthLabel };
  }, [history]);

  const renderContent = () => {
    switch (selectedPage) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="si-h1">Propinas del día</h2>
              <p className="si-body mt-1">Cargá las propinas y cerrá el día distribuyéndolas entre el equipo.</p>
            </div>

            <div className="si-card p-6 relative overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10 blur-2xl"
                style={{ background: '#c8f06a' }}
              />
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div>
                  <div className="si-eyebrow mb-2">Total acumulado hoy</div>
                  <div className="si-display flex items-center gap-3" style={{ color: '#c8f06a' }}>
                    {loadingDailyTips ? (
                      <Spinner size={28} className="text-ink-3" />
                    ) : (
                      <span><Money value={dailyTips} /></span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
                  <div className="flex gap-2 flex-1 sm:flex-initial">
                    <input
                      type="number"
                      placeholder="Ingresar propina"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value)}
                      className="si-input flex-1 sm:w-44"
                      disabled={isAddingTip}
                    />
                    <button onClick={handleAddTip} disabled={isAddingTip} className="si-btn-primary whitespace-nowrap">
                      {isAddingTip ? <Spinner size={14} className="mr-2" /> : <FaPlus className="mr-2" />}
                      {isAddingTip ? 'Agregando...' : 'Agregar'}
                    </button>
                  </div>
                  <button onClick={openEditTipModal} className="si-btn-secondary whitespace-nowrap">
                    <FaPen className="mr-2 text-xs" />
                    Editar total
                  </button>
                </div>
              </div>
            </div>

            {/* Widgets del dashboard */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Widget 1 — Top mozos del mes */}
              <div className="si-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="si-eyebrow">Top mozos</div>
                    <div className="text-sm font-semibold text-ink mt-0.5 capitalize">{dashboardWidgets.monthLabel}</div>
                  </div>
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center"
                    style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                  >
                    <FaUserFriends style={{ color: '#b28cf5' }} />
                  </div>
                </div>
                {dashboardWidgets.topEmployees.length === 0 ? (
                  <div className="si-caption py-6 text-center">
                    Sin datos del mes todavía.
                  </div>
                ) : (
                  <RankingMozos items={dashboardWidgets.topEmployees} top={5} />
                )}
              </div>

              {/* Widget 2 — Últimos 7 días */}
              <div className="si-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="si-eyebrow">Últimos 7 días</div>
                    <div className="text-sm font-semibold text-ink mt-0.5">
                      <Money value={dashboardWidgets.last7Days.reduce((s, d) => s + d.total, 0)} />
                    </div>
                  </div>
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center"
                    style={{ background: 'rgba(200, 240, 106, 0.12)' }}
                  >
                    <FaChartBar style={{ color: '#c8f06a' }} />
                  </div>
                </div>
                {dashboardWidgets.last7Days.every((d) => d.total === 0) ? (
                  <div className="text-sm text-ink-3 py-10 text-center">
                    Todavía no cargaste propinas esta semana.
                  </div>
                ) : (
                  <div style={{ height: 160 }}>
                    <Line
                      data={{
                        labels: dashboardWidgets.last7Days.map((d) => d.label),
                        datasets: [
                          {
                            data: dashboardWidgets.last7Days.map((d) => d.total),
                            borderColor: '#b28cf5',
                            backgroundColor: 'rgba(178, 140, 245, 0.15)',
                            pointBackgroundColor: '#b28cf5',
                            pointBorderColor: '#b28cf5',
                            tension: 0.35,
                            fill: true,
                            borderWidth: 2,
                            pointRadius: 3,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: '#17171d',
                            borderColor: '#26262e',
                            borderWidth: 1,
                            titleColor: '#f5f5f7',
                            bodyColor: '#c7c7cf',
                            padding: 10,
                            callbacks: {
                              label: (ctx) => money(Number(ctx.parsed.y)),
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: '#7a7a82', font: { size: 10 } },
                          },
                          y: {
                            grid: { color: 'rgba(38, 38, 46, 0.6)' },
                            ticks: {
                              color: '#7a7a82',
                              font: { size: 10 },
                              callback: (v) => `$${v}`,
                            },
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Widget 3 — Este mes vs mes anterior */}
              <div className="si-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="si-eyebrow">Este mes</div>
                    <div className="text-sm font-semibold text-ink mt-0.5 capitalize">
                      {dashboardWidgets.monthLabel} vs anterior
                    </div>
                  </div>
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center"
                    style={{ background: 'rgba(245, 158, 11, 0.12)' }}
                  >
                    <FaCalendarCheck style={{ color: '#f59e0b' }} />
                  </div>
                </div>
                <div className="text-4xl font-bold tracking-tightish text-ink mb-2">
                  <Money value={dashboardWidgets.thisMonth} />
                </div>
                {dashboardWidgets.deltaPct === null ? (
                  <div className="text-xs text-ink-3 mb-4">
                    Sin datos del mes anterior para comparar.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="inline-flex items-center gap-1 text-sm font-semibold px-2 py-0.5 rounded-md"
                      style={{
                        background:
                          dashboardWidgets.deltaPct >= 0
                            ? 'rgba(200, 240, 106, 0.12)'
                            : 'rgba(239, 68, 68, 0.12)',
                        color: dashboardWidgets.deltaPct >= 0 ? '#c8f06a' : '#ef4444',
                      }}
                    >
                      {dashboardWidgets.deltaPct >= 0 ? '▲' : '▼'}
                      {Math.abs(dashboardWidgets.deltaPct).toFixed(1)}%
                    </span>
                    <span className="text-xs text-ink-3">vs mes anterior</span>
                  </div>
                )}
                <div className="pt-3 border-t border-line">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-3">Mes anterior</span>
                    <span className="text-ink font-medium">
                      <Money value={dashboardWidgets.lastMonth} />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="si-card overflow-hidden">
              <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="si-h2">Lista de mozos</h3>
                  <p className="text-xs text-ink-3 mt-0.5">
                    {modoReparto === MODO_PUNTOS
                      ? 'Marcá quiénes trabajaron. El pozo se divide por el total de puntos presentes.'
                      : 'Marcá los empleados que participaron y asigná porcentajes o monto fijo.'}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Selector de modo de reparto */}
                  <div className="flex items-center p-1 rounded-lg bg-surface-2 border border-line">
                    {[
                      [MODO_PORCENTAJE, 'Porcentaje'],
                      [MODO_PUNTOS, 'Puntos'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => handleChangeModo(key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                          modoReparto === key
                            ? 'bg-purple text-black'
                            : 'text-ink-3 hover:text-ink'
                        }`}
                        title={key === MODO_PUNTOS
                          ? 'Cada mozo vale N puntos y el pozo se divide proporcionalmente'
                          : 'Cada mozo se lleva un % del pozo'}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {modoReparto === MODO_PUNTOS ? (
                    <div className="text-sm font-medium text-ink-2">
                      <span className={totalPuntosSelected > 0 ? 'text-lime' : 'text-warn'}>
                        {fmtPuntos(totalPuntosSelected)} pts
                      </span>
                      {totalPuntosSelected > 0 && (
                        <span className="text-ink-3">
                          {' '}· punto = <Money value={valorPunto} decimales />
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={`text-sm font-medium ${
                      totalPercentageSelected === 100
                        ? 'text-lime'
                        : totalPercentageSelected > 100
                          ? 'text-danger'
                          : 'text-warn'
                    }`}>
                      Total: {totalPercentageSelected}%
                    </div>
                  )}

                  <button onClick={distributeEqually} className="si-btn-secondary">
                    <FaEquals className="mr-2 text-xs" />
                    Distribuir igual
                  </button>
                </div>
              </div>

              {/* Búsqueda mozos para cerrar día */}
              <div className="px-6 py-3 border-b border-line flex items-center gap-3 flex-wrap bg-surface-2/30">
                <div className="relative flex-1 min-w-[220px]">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-xs" />
                  <input
                    type="text"
                    placeholder="Buscar mozo por nombre, apellido o CI..."
                    value={closeDaySearch}
                    onChange={(e) => setCloseDaySearch(e.target.value)}
                    className="si-input pl-9 w-full py-1.5 text-sm"
                  />
                </div>
                {closeDaySearch && (
                  <button
                    onClick={() => setCloseDaySearch('')}
                    className="text-xs text-ink-3 hover:text-ink transition"
                  >
                    Limpiar
                  </button>
                )}
                <span className="text-xs text-ink-3">
                  {selectedEmployees.length} seleccionado(s)
                </span>
              </div>

              {loadingEmployees ? (
                <LoadingScreen label="Cargando mozos..." />
              ) : (() => {
                const closeDaySearchLower = closeDaySearch.trim().toLowerCase();
                // En el cierre del dia van solo los que estan trabajando.
                const empleadosParaCerrar = employees.filter((e) => e.activo !== false);
                const visibleEmployees = closeDaySearchLower
                  ? empleadosParaCerrar.filter(e =>
                      `${e.Nombre || ''} ${e.Apellido || ''}`.toLowerCase().includes(closeDaySearchLower) ||
                      String(e.Ci || '').toLowerCase().includes(closeDaySearchLower)
                    )
                  : empleadosParaCerrar;
                return (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-line bg-surface-2/40">
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Sel</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Nombre</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Apellido</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Monto fijo</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">
                          {modoReparto === MODO_PUNTOS ? 'Puntos' : 'Porcentaje'}
                        </th>
                        <th className="py-3 px-5 text-right font-semibold text-xs uppercase tracking-wider text-ink-3">Le toca</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEmployees.map((employee, idx) => {
                        const sel = selectedEmployees.find((emp) => emp.id === employee.id);
                        const checked = !!sel;
                        return (
                          <tr
                            key={employee.id}
                            className={`${idx !== visibleEmployees.length - 1 ? 'border-b border-line' : ''} ${checked ? 'bg-purple/5' : 'hover:bg-surface-2/40'} transition-colors`}
                          >
                            <td className="py-3 px-5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleEmployee(employee.id)}
                                className="form-checkbox h-4 w-4 rounded border-line bg-surface text-purple focus:ring-purple"
                                style={{ accentColor: '#b28cf5' }}
                              />
                            </td>
                            <td className="py-3 px-5 text-sm text-ink">{employee.Nombre}</td>
                            <td className="py-3 px-5 text-sm text-ink">{employee.Apellido}</td>
                            <td className="py-3 px-5">
                              <input
                                type="number"
                                placeholder="0"
                                value={sel?.specificAmount || 0}
                                onChange={(e) => handleSetSpecificAmount(employee.id, e.target.value)}
                                className="si-input w-28 py-1.5 text-sm"
                              />
                            </td>
                            <td className="py-3 px-5">
                              {modoReparto === MODO_PUNTOS ? (
                                <div className="flex items-center gap-1">
                                  {/* type="text" y no "number": el input
                                      numérico del navegador rechaza la coma,
                                      que es como se escribe el decimal acá.
                                      inputMode="decimal" igual saca el teclado
                                      numérico en el celular. */}
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={sel?.puntos ?? ''}
                                    onChange={(e) => handleSetPuntos(employee.id, e.target.value)}
                                    disabled={!checked}
                                    className="si-input w-20 py-1.5 text-sm disabled:opacity-40 si-money"
                                  />
                                  <span className="text-ink-3 text-sm">pts</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={sel?.percentage || 0}
                                    onChange={(e) => handleSelectEmployee(employee.id, parseInt(e.target.value) || 0)}
                                    className="si-input w-20 py-1.5 text-sm"
                                  />
                                  <span className="text-ink-3 text-sm">%</span>
                                </div>
                              )}
                            </td>
                            {/* Vista previa de lo que se va a acreditar, con el
                                mismo redondeo que hace el backend. */}
                            <td className="py-3 px-5 text-right">
                              {checked ? (
                                <span className="text-sm font-semibold" style={{ color: '#c8f06a' }}>
                                  ${Math.round(previewMontos[employee.id] || 0).toLocaleString('es-UY')}
                                </span>
                              ) : (
                                <span className="text-sm text-ink-3">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {visibleEmployees.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-ink-3 text-sm">
                            {employees.length === 0
                              ? 'Agregá tu primer mozo para empezar a repartir.'
                              : 'Ningún mozo coincide con la búsqueda.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>

            <div className="flex items-center justify-end gap-3">
              {!canCloseDay && selectedEmployees.length > 0 && (
                <span className="text-xs text-ink-3">
                  {modoReparto === MODO_PUNTOS
                    ? 'Todos los mozos marcados necesitan puntos (o un monto fijo) para cerrar'
                    : totalPercentageSelected === 100
                      ? ''
                      : `Falta ajustar ${100 - totalPercentageSelected}% para cerrar`}
                </span>
              )}
              <button
                onClick={handleCloseDay}
                disabled={!canCloseDay || isClosingDay}
                className="si-btn-success px-6 py-3"
                title={canCloseDay
                  ? 'Cerrar día y distribuir'
                  : modoReparto === MODO_PUNTOS
                    ? 'Asigná puntos a los mozos seleccionados'
                    : 'El total de porcentajes debe ser 100%'}
              >
                {isClosingDay ? <Spinner size={14} className="mr-2" /> : <FaCalendarCheck className="mr-2" />}
                {isClosingDay ? 'Cerrando...' : 'Cerrar el día'}
              </button>
            </div>
          </div>
        );

      case 'users': {
        const employeeSearchLower = employeeListSearch.trim().toLowerCase();
        // Las bajas no se muestran salvo que se pidan: son el pasado del
        // equipo y ensucian la lista del dia a dia.
        const baseEmpleados = verBajas ? employees : empleadosActivos;
        const filteredEmployeesList = employeeSearchLower
          ? baseEmpleados.filter(e =>
              `${e.Nombre || ''} ${e.Apellido || ''}`.toLowerCase().includes(employeeSearchLower) ||
              String(e.Ci || '').toLowerCase().includes(employeeSearchLower) ||
              String(e.email || '').toLowerCase().includes(employeeSearchLower)
            )
          : baseEmpleados;
        return (
          <div className="space-y-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h2 className="si-h1">Gestión de mozos</h2>
                <p className="si-body mt-1">Controlá y administrá a tus empleados.</p>
                {saldoDeBajas > 0 && (
                  <p className="si-caption mt-2" style={{ color: '#f59e0b' }}>
                    Hay <strong><Money value={saldoDeBajas} /></strong> sin retirar a nombre de{' '}
                    {empleadosDeBaja.filter((e) => (e.propina || 0) > 0).length} mozo(s) dados de baja.
                    Mientras estén de baja no pueden pedirlo: liquidáselo desde la lista.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="si-card px-4 py-2.5 flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-ink-3">Total acumulado</span>
                  <span className="font-bold" style={{ color: '#c8f06a' }}>
                    <Money value={calculateTotalPropinasAcumuladas()} />
                  </span>
                </div>
                <button
                  onClick={() => setVerBajas((v) => !v)}
                  className="si-btn-secondary"
                  title="Mostrar u ocultar a los mozos dados de baja"
                >
                  {verBajas ? 'Ocultar bajas' : `Ver bajas (${empleadosDeBaja.length})`}
                </button>
                <button onClick={openModal} className="si-btn-primary">
                  <FaPlus className="mr-2" />
                  Agregar mozo
                </button>
              </div>
            </div>

            {/* Búsqueda */}
            <div className="si-card p-4 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-xs" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, apellido, CI o email..."
                  value={employeeListSearch}
                  onChange={(e) => setEmployeeListSearch(e.target.value)}
                  className="si-input pl-9 w-full"
                />
              </div>
              <span className="text-xs text-ink-3">
                {filteredEmployeesList.length} de {employees.length} mozo(s)
              </span>
              {employeeListSearch && (
                <button
                  onClick={() => setEmployeeListSearch('')}
                  className="text-xs text-ink-3 hover:text-ink transition"
                >
                  Limpiar
                </button>
              )}
            </div>

            <div className="si-card overflow-hidden">
              {loadingEmployees ? (
                <LoadingScreen label="Cargando mozos..." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-line bg-surface-2/40">
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Documento</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Nombre</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Apellido</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Email</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Teléfono</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Cuenta</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3" title="Peso del mozo en el reparto por puntos">Puntos</th>
                        <th className="py-3 px-5 text-right font-semibold text-xs uppercase tracking-wider text-ink-3">Propina</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployeesList.map((employee, idx) => (
                        <tr
                          key={employee.id}
                          className={`${idx !== filteredEmployeesList.length - 1 ? 'border-b border-line' : ''} hover:bg-surface-2/40 transition-colors ${employee.activo === false ? 'opacity-60' : ''}`}
                        >
                          <td className="py-3 px-5 text-sm text-ink-2">{employee.Ci}</td>
                          <td className="py-3 px-5 text-sm text-ink">
                            {employee.Nombre}
                            {employee.activo === false && (
                              <span className="ml-2 si-pill text-[10px] px-2 py-0.5">De baja</span>
                            )}
                          </td>
                          <td className="py-3 px-5 text-sm text-ink">{employee.Apellido}</td>
                          <td className="py-3 px-5 text-sm text-ink-2">{employee.email}</td>
                          <td className="py-3 px-5 text-sm text-ink-2">{employee.telefono}</td>
                          <td className="py-3 px-5 text-sm text-ink-2">{employee.nCuenta}</td>
                          <td className="py-3 px-5 text-sm text-ink-2">{fmtPuntos(employee.puntos ?? 1)}</td>
                          <td className="py-3 px-5 text-sm font-semibold text-right" style={{ color: '#c8f06a' }}>
                            <Money value={employee.propina} />
                          </td>
                          <td className="py-3 px-5">
                            <div className="flex items-center gap-2 flex-wrap">
                              {employee.activo === false ? (
                                <>
                                  <button
                                    onClick={() => handleReactivarEmployee(employee)}
                                    className="px-2.5 py-1.5 rounded-lg bg-surface-2 border border-line text-ink-2 hover:text-ink hover:bg-line text-xs font-medium transition"
                                    title="Volver a darlo de alta. Conserva su saldo."
                                  >
                                    Reactivar
                                  </button>
                                  {(employee.propina || 0) > 0 && (
                                    <button
                                      onClick={() => setConfirmLiquidar({ open: true, employee })}
                                      className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition border"
                                      style={{ background: 'rgba(200, 240, 106, 0.12)', borderColor: 'rgba(200, 240, 106, 0.3)', color: '#c8f06a' }}
                                      title="Registrar que se le pagó lo que tenía acumulado"
                                    >
                                      Liquidar saldo
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => openEditModal(employee)}
                                    className="px-2.5 py-1.5 rounded-lg bg-surface-2 border border-line text-ink-2 hover:text-ink hover:bg-line text-xs font-medium transition flex items-center gap-1"
                                    title="Editar"
                                  >
                                    <FaPen className="text-[10px]" />
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => requestDeleteEmployee(employee)}
                                    className="px-2.5 py-1.5 rounded-lg bg-danger/15 border border-danger/30 text-danger hover:bg-danger/25 text-xs font-medium transition flex items-center gap-1"
                                    title="Dar de baja"
                                  >
                                    <FaTrashAlt className="text-[10px]" />
                                    Dar de baja
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredEmployeesList.length === 0 && (
                        <tr>
                          <td colSpan={9} className="text-center py-8 text-ink-3 text-sm">
                            {employees.length === 0
                              ? 'Agregá tu primer mozo desde el botón de arriba.'
                              : 'Ningún mozo coincide con la búsqueda.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'history': {
        const dateFiltered = history.filter(entry => {
          if (!startDate || !endDate) return true;
          const d = new Date(entry.fecha);
          return d >= startOfDayMVD(startDate) && d <= endOfDayMVD(endDate);
        });

        const historySearchLower = historySearch.trim().toLowerCase();
        const filteredHistory = historySearchLower
          ? dateFiltered.filter(entry => {
              // match por fecha formateada (DD/MM/AAAA, "lunes", nombre del mes)
              const fecha = new Date(entry.fecha);
              const fechaStr = fecha.toLocaleDateString('es-ES', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                timeZone: 'America/Montevideo',
              }).toLowerCase();
              if (fechaStr.includes(historySearchLower)) return true;
              // match por monto
              if (String(entry.propina).includes(historySearchLower)) return true;
              // match por nombre/apellido en detalles
              try {
                const detalles = JSON.parse(entry.detalles || '[]');
                return detalles.some((d) =>
                  `${d.nombre || ''} ${d.apellido || ''}`.toLowerCase().includes(historySearchLower)
                );
              } catch {
                return false;
              }
            })
          : dateFiltered;

        const totalBetween = filteredHistory.reduce((sum, e) => sum + e.propina, 0);

        // Paginación
        const totalHistoryPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE));
        const currentHistoryPage = Math.min(historyPage, totalHistoryPages);
        const historyStartIdx = (currentHistoryPage - 1) * HISTORY_PAGE_SIZE;
        const visibleHistory = filteredHistory.slice(historyStartIdx, historyStartIdx + HISTORY_PAGE_SIZE);

        return (
          <div className="space-y-6">
            <div>
              <h2 className="si-h1">Historial de cierres</h2>
              <p className="si-body mt-1">Cierres de día y la distribución detallada por mozo.</p>
            </div>

            <div className="si-card p-5 space-y-4">
              <DateRangePresets active={historyPreset} onChange={applyHistoryPreset} />
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="si-label">Desde</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => { setStartDate(e.target.value); setHistoryPreset('custom'); setHistoryPage(1); }}
                    className="si-input"
                  />
                </div>
                <div>
                  <label className="si-label">Hasta</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => { setEndDate(e.target.value); setHistoryPreset('custom'); setHistoryPage(1); }}
                    className="si-input"
                  />
                </div>
                <div className="ml-auto">
                  <div className="text-xs uppercase tracking-wider text-ink-3 mb-1">Total filtrado</div>
                  <div className="text-2xl font-bold" style={{ color: '#c8f06a' }}>
                    <Money value={totalBetween} />
                  </div>
                </div>
              </div>
              {/* Búsqueda */}
              <div className="flex items-center gap-3 flex-wrap pt-3 border-t border-line">
                <div className="relative flex-1 min-w-[260px]">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-xs" />
                  <input
                    type="text"
                    placeholder="Buscar por fecha, monto o nombre de mozo..."
                    value={historySearch}
                    onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                    className="si-input pl-9 w-full"
                  />
                </div>
                <span className="text-xs text-ink-3">
                  {filteredHistory.length} cierre(s)
                </span>
                {historySearch && (
                  <button
                    onClick={() => { setHistorySearch(''); setHistoryPage(1); }}
                    className="text-xs text-ink-3 hover:text-ink transition"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>

            {loadingHistory ? (
              <LoadingScreen label="Cargando historial..." />
            ) : filteredHistory.length === 0 ? (
              <div className="si-card p-10 text-center text-ink-3">
                {history.length === 0
                  ? 'Acá van a aparecer los cierres una vez que cierres el primer día.'
                  : 'Ningún cierre coincide con el filtro.'}
              </div>
            ) : (
              <>
                <div className="grid gap-4">
                  {visibleHistory.map((entry) => {
                    const isOpen = openCards[entry.Id] || false;
                    const toggleOpen = () => toggleCard(entry.Id);
                    const fecha = new Date(entry.fecha);
                    const diaSemana = fecha.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'America/Montevideo' });
                    const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                    const fechaFormateada = fecha.toLocaleDateString('es-ES', {
                      year: 'numeric', month: 'long', day: 'numeric',
                      timeZone: 'America/Montevideo',
                    });
                    const hora = fecha.toLocaleTimeString('es-ES', {
                      hour: '2-digit', minute: '2-digit',
                      timeZone: 'America/Montevideo',
                    });

                    return (
                      <div key={entry.Id} className="si-card overflow-hidden">
                        <button
                          onClick={toggleOpen}
                          className="w-full text-left px-5 py-4 flex justify-between items-center hover:bg-surface-2/50 transition-colors"
                        >
                          <div>
                            <h3 className="si-h3">
                              {diaCapitalizado}, {fechaFormateada}
                            </h3>
                            <p className="text-xs text-ink-3 mt-0.5">{hora} hs</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg" style={{ color: '#c8f06a' }}>
                              <Money value={entry.propina} />
                            </span>
                            {isOpen ? (
                              <FaChevronUp className="text-ink-3" />
                            ) : (
                              <FaChevronDown className="text-ink-3" />
                            )}
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-5 pt-1 border-t border-line">
                            <div className="grid gap-2 mt-3">
                              {(() => {
                                const detalles = parseDetalles(entry.detalles);
                                return detalles.map((detail, index) => (
                                <div
                                  key={index}
                                  className="bg-surface-2 border border-line rounded-xl px-4 py-3 flex justify-between items-center gap-3"
                                >
                                  <div>
                                    <p className="font-medium text-ink text-sm">
                                      {detail.nombre} {detail.apellido}
                                    </p>
                                    <p className="text-xs text-ink-3 mt-0.5">
                                      {detail.specificAmount > 0 && (
                                        <span>Monto fijo: <strong className="text-ink-2"><Money value={detail.specificAmount} /></strong></span>
                                      )}
                                      {detail.specificAmount > 0 && (detail.puntos > 0 || detail.porcentaje > 0) && ' · '}
                                      {/* En los cierres por puntos mostramos los puntos (que es
                                          lo que el restaurante cargó) y no el % equivalente. */}
                                      {detail.puntos !== undefined && detail.puntos !== null ? (
                                        detail.puntos > 0 && (
                                          <span>
                                            <strong className="text-ink-2">{fmtPuntos(detail.puntos)} pts</strong>
                                            {detail.valorPunto ? ` · punto ${money(Number(detail.valorPunto), { decimales: true })}` : ''}
                                          </span>
                                        )
                                      ) : (
                                        detail.porcentaje > 0 && (
                                          <span><strong className="text-ink-2">{detail.porcentaje}%</strong> del pozo</span>
                                        )
                                      )}
                                    </p>
                                  </div>
                                  {/* Monto acreditado: viene del cierre para los nuevos y se
                                      recalcula con la fórmula vieja para los históricos. */}
                                  <span className="text-sm font-semibold whitespace-nowrap" style={{ color: '#c8f06a' }}>
                                    ${Math.round(computePayout(detail, entry.propina, detalles)).toLocaleString('es-UY')}
                                  </span>
                                </div>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Paginación */}
                {filteredHistory.length > HISTORY_PAGE_SIZE && (
                  <div className="si-card px-5 py-3 flex items-center justify-between flex-wrap gap-3 text-sm">
                    <span className="text-ink-3">
                      Mostrando {historyStartIdx + 1}-{Math.min(historyStartIdx + HISTORY_PAGE_SIZE, filteredHistory.length)} de {filteredHistory.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={currentHistoryPage === 1}
                        className="px-3 py-1.5 rounded-lg border border-line bg-surface text-ink-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                      >
                        <FaArrowLeft className="text-[10px]" />
                        Anterior
                      </button>
                      <span className="text-ink-2 px-2">Página {currentHistoryPage} de {totalHistoryPages}</span>
                      <button
                        onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                        disabled={currentHistoryPage === totalHistoryPages}
                        className="px-3 py-1.5 rounded-lg border border-line bg-surface text-ink-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                      >
                        Siguiente
                        <FaArrowRight className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }

      case 'stats': {
        const filteredStats = history.filter(entry => {
          if (!startDate || !endDate) return true;
          const d = new Date(entry.fecha);
          return d >= startOfDayMVD(startDate) && d <= endOfDayMVD(endDate);
        });
        return (
          <div className="space-y-6">
            <div>
              <h2 className="si-h1">Estadísticas</h2>
              <p className="si-body mt-1">Visualizá la evolución de las propinas y la distribución por mozo.</p>
            </div>

            <div className="si-card p-5 space-y-4">
              <DateRangePresets active={statsPreset} onChange={applyStatsPreset} />
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="si-label">Desde</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => { setStartDate(e.target.value); setStatsPreset('custom'); }}
                    className="si-input"
                  />
                </div>
                <div>
                  <label className="si-label">Hasta</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => { setEndDate(e.target.value); setStatsPreset('custom'); }}
                    className="si-input"
                  />
                </div>
              </div>
            </div>

            {loadingHistory ? (
              <LoadingScreen label="Cargando datos..." />
            ) : (
              <div className="grid gap-6">
                <div className="si-card p-5">
                  <h3 className="si-h3 mb-4">Propinas por día</h3>
                  <div style={{ height: '380px' }}>
                    <Line
                      data={generateChartData(filteredStats)}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                          // Sin leyenda: hay una sola serie y el título ya la nombra.
                          legend: { display: false },
                          // Mismo tooltip que el resto de la app (antes este
                          // gráfico usaba la caja negra por defecto de Chart.js).
                          tooltip: {
                            backgroundColor: '#17171d',
                            borderColor: '#26262e',
                            borderWidth: 1,
                            titleColor: '#f5f5f7',
                            bodyColor: '#c7c7cf',
                            padding: 10,
                            callbacks: { label: (ctx) => money(Number(ctx.parsed.y)) },
                          },
                        },
                        scales: {
                          // Sin grilla vertical: en una serie de tiempo no aporta
                          // nada y compite con los datos.
                          x: { grid: { display: false }, ticks: { color: '#8a8a94' } },
                          y: {
                            grid: { color: 'rgba(38,38,46,0.5)' },
                            ticks: { color: '#8a8a94', callback: (v) => money(v) },
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="si-card p-5">
                  <h3 className="si-h3">Por día de semana</h3>
                  <p className="si-caption mt-1 mb-4">
                    Qué días rinden más, en el período seleccionado.
                  </p>
                  <BarrasDiaSemana history={filteredStats} />
                </div>

                {/* Este NO usa filteredStats: su horizonte es fijo a propósito
                    (ver el comentario del componente). */}
                <div className="si-card p-5">
                  <h3 className="si-h3">Mes a mes</h3>
                  <p className="si-caption mt-1 mb-4">
                    Últimos 12 meses. No depende del filtro de arriba, para que la
                    comparación entre meses siempre sea la misma.
                  </p>
                  <GraficoMeses history={history} />
                </div>

                <div className="si-card p-5">
                  <h3 className="si-h3">Cuánto le tocó a cada mozo</h3>
                  <p className="si-caption mt-1 mb-4">
                    Total repartido en el período seleccionado, de mayor a menor.
                  </p>
                  <RankingMozos items={rankingPorMozo(filteredStats)} mostrarPorcentaje />
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'payouts':
        return (
          <div className="space-y-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="si-h1">Pagos efectuados</h2>
                <p className="si-body mt-1">Reporte de retiros pagados por período.</p>
              </div>
              <button
                onClick={downloadPdf}
                disabled={isDownloadingPdf}
                className="si-btn-secondary"
              >
                {isDownloadingPdf ? <Spinner size={14} className="mr-2" /> : <FaFilePdf className="mr-2" />}
                {isDownloadingPdf ? 'Generando...' : 'Exportar PDF'}
              </button>
            </div>

            <div className="si-card p-5 space-y-4">
              <DateRangePresets active={payoutPreset} onChange={applyPayoutPreset} />
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <label className="si-label">Desde</label>
                  <input type="date" value={payoutStart} onChange={e => { setPayoutStart(e.target.value); setPayoutPreset('custom'); }} className="si-input" />
                </div>
                <div>
                  <label className="si-label">Hasta</label>
                  <input type="date" value={payoutEnd} onChange={e => { setPayoutEnd(e.target.value); setPayoutPreset('custom'); }} className="si-input" />
                </div>
                <button onClick={() => fetchPayoutReport({})} disabled={loadingPayouts} className="si-btn-primary">
                  {loadingPayouts ? <Spinner size={14} className="mr-2" /> : null}
                  Aplicar filtro
                </button>
                <div className="ml-auto text-right">
                  <div className="text-xs uppercase tracking-wider text-ink-3 mb-1">Total período</div>
                  <div className="text-2xl font-bold" style={{ color: '#c8f06a' }}>
                    <Money value={Number(payoutReport?.totalPeriodo || 0)} />
                  </div>
                </div>
              </div>
            </div>

            {loadingPayouts ? (
              <LoadingScreen label="Cargando reporte..." />
            ) : !payoutReport || payoutReport.empleados.length === 0 ? (
              <div className="si-card p-10 text-center text-ink-3">
                No se pagó nada en este período. Probá ampliando el rango de fechas.
              </div>
            ) : (
              <div className="grid gap-3">
                {payoutReport.empleados.map(emp => {
                  const open = payoutOpen[emp.empleadoId];
                  return (
                    <div key={emp.empleadoId} className="si-card overflow-hidden">
                      <button
                        className="w-full text-left px-5 py-4 flex justify-between items-center hover:bg-surface-2/50 transition-colors"
                        onClick={() => setPayoutOpen(prev => ({ ...prev, [emp.empleadoId]: !open }))}
                      >
                        <div>
                          <div className="font-semibold text-ink text-sm">
                            {emp.nombre} {emp.apellido}
                          </div>
                          <div className="text-xs text-ink-3 mt-0.5">
                            CI {emp.ci} · {emp.retiros.length} retiro{emp.retiros.length === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold" style={{ color: '#c8f06a' }}>
                            <Money value={Number(emp.totalPagado)} />
                          </span>
                          {open ? <FaChevronUp className="text-ink-3" /> : <FaChevronDown className="text-ink-3" />}
                        </div>
                      </button>
                      {open && (
                        <div className="px-5 pb-5 border-t border-line">
                          <table className="w-full text-sm mt-3">
                            <thead>
                              <tr className="text-left text-ink-3 border-b border-line">
                                <th className="py-2 font-medium text-xs uppercase tracking-wider">Retiro ID</th>
                                <th className="py-2 font-medium text-xs uppercase tracking-wider">Fecha</th>
                                <th className="py-2 font-medium text-xs uppercase tracking-wider">Monto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {emp.retiros.map(r => {
                                const d = new Date(r.fecha);
                                const dateStr = d.toLocaleString('es-ES', { timeZone: 'America/Montevideo' });
                                return (
                                  <tr key={r.retiroId} className="border-b border-line/60 last:border-0">
                                    <td className="py-2 text-ink-2">#{r.retiroId}</td>
                                    <td className="py-2 text-ink-2">{dateStr}</td>
                                    <td className="py-2 font-semibold" style={{ color: '#c8f06a' }}>
                                      <Money value={Number(r.propina)} />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'cierres':
        return <GenerarCierrePage />;

      default:
        return null;
    }
  };

  const NavButton = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setSelectedPage(id)}
      title={sidebarCollapsed ? label : undefined}
      aria-label={label}
      className={`flex items-center w-full ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'text-left px-3 py-2.5'} rounded-xl text-sm font-medium transition-all ${
        selectedPage === id
          ? 'bg-purple/15 text-purple border border-purple/30'
          : 'text-ink-2 hover:text-ink hover:bg-surface-2 border border-transparent'
      }`}
    >
      <Icon className={`${sidebarCollapsed ? '' : 'mr-3'} text-base`} />
      {!sidebarCollapsed && label}
    </button>
  );

  return (
    <div className="flex h-screen bg-bg text-ink">
      <aside className={`relative bg-surface border-r border-line ${sidebarCollapsed ? 'w-20 px-3' : 'w-64 px-5'} py-5 flex flex-col flex-shrink-0 transition-all duration-200`}>
        <button
          onClick={toggleSidebar}
          title={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'}
          aria-label={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'}
          className="absolute -right-3 top-7 w-6 h-6 rounded-full bg-surface-2 border border-line text-ink-2 hover:text-ink hover:bg-surface flex items-center justify-center transition-all shadow-sm z-10"
        >
          {sidebarCollapsed ? <FaChevronRight className="text-[10px]" /> : <FaChevronLeft className="text-[10px]" />}
        </button>

        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3 px-1'} mb-8`}>
          <img
            src={logo}
            alt="ServiceIn"
            className="w-9 h-9 rounded-lg object-contain flex-shrink-0"
          />
          {!sidebarCollapsed && (
            <div>
              <div className="si-h3 leading-tight">ServiceIn</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3">Restaurante</div>
            </div>
          )}
        </div>

        <nav className="space-y-1.5 flex-1">
          <NavButton id="dashboard" icon={FaMoneyBill} label="Propinas del día" />
          <NavButton id="users" icon={FaUserFriends} label="Gestión de mozos" />
          <NavButton id="history" icon={FaCalendarCheck} label="Historial" />
          <NavButton id="stats" icon={FaChartBar} label="Estadísticas" />
          <NavButton id="cierres" icon={FaFileInvoiceDollar} label="Generar pagos" />
          {/* "Reporte pagos" oculto: era del modelo viejo (lo que el restaurante
              le debía a ServiceIn). El código de la sección (case 'payouts') y sus
              fetchers se dejan por si se reimplementa más adelante. */}
          {/* <NavButton id="payouts" icon={FaFileInvoiceDollar} label="Reporte pagos" /> */}
        </nav>

        <button
          onClick={handleLogout}
          title={sidebarCollapsed ? 'Cerrar sesión' : undefined}
          aria-label="Cerrar sesión"
          className={`si-btn-danger w-full mt-4 ${sidebarCollapsed ? '!px-0 justify-center' : ''}`}
        >
          <FaSignOutAlt className={sidebarCollapsed ? '' : 'mr-2'} />
          {!sidebarCollapsed && 'Cerrar sesión'}
        </button>
      </aside>

      <div className="flex-1 overflow-y-auto">
        <main className="p-6 lg:p-8">{renderContent()}</main>
      </div>

      {/* Modal de agregar empleado */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={!isSubmittingEmployee}
        className="bg-surface border border-line rounded-2xl shadow-card mx-4 max-w-md w-full p-0 overflow-hidden relative outline-none"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <h2 className="si-h2">Agregar nuevo mozo</h2>
          <button onClick={closeModal} disabled={isSubmittingEmployee} className="text-ink-3 hover:text-ink p-1 transition disabled:opacity-40" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>
        <form onSubmit={handleAddEmployee} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <ModalField
            label="CI o pasaporte"
            name="Ci"
            value={newEmployee.Ci}
            onChange={handleInputChange}
            hint="Los extranjeros pueden usar el número de pasaporte, con letras."
          />
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Nombre" name="Nombre" value={newEmployee.Nombre} onChange={handleInputChange} />
            <ModalField label="Apellido" name="Apellido" value={newEmployee.Apellido} onChange={handleInputChange} />
          </div>
          <ModalField type="email" label="Email" name="email" value={newEmployee.email} onChange={handleInputChange} />
          <ModalField label="Teléfono" name="telefono" value={newEmployee.telefono} onChange={handleInputChange} />
          <ModalField type="password" label="Contraseña" name="password" value={newEmployee.password} onChange={handleInputChange} />
          <ModalField
            type="text"
            inputMode="decimal"
            label="Puntos (reparto por puntos)"
            name="puntos"
            value={newEmployee.puntos}
            onChange={(e) => setNewEmployee({ ...newEmployee, puntos: limpiarNumero(e.target.value) })}
            required={false}
            hint="Cuánto vale en el reparto por puntos: 1 un mozo, 0,5 medio punto. Se usa sólo si el restaurante reparte por puntos."
          />

          <div className="pt-2 border-t border-line">
            <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">Datos bancarios</div>
            <div className="grid grid-cols-2 gap-3">
              <ModalSelect label="Banco" name="banco" value={newEmployee.banco} onChange={handleInputChange} options={BANCOS_UY} required={false} />
              <ModalSelect label="Tipo de cuenta" name="tipoCuenta" value={newEmployee.tipoCuenta} onChange={handleInputChange} options={TIPOS_CUENTA} required={false} />
            </div>
            <div className="mt-3">
              <ModalField label="Número de cuenta" name="nCuenta" value={newEmployee.nCuenta} onChange={handleInputChange} required={false} />
            </div>
          </div>

          <button type="submit" disabled={isSubmittingEmployee} className="si-btn-primary w-full mt-2">
            {isSubmittingEmployee && <Spinner size={14} className="mr-2" />}
            {isSubmittingEmployee ? 'Agregando...' : 'Agregar mozo'}
          </button>
        </form>
      </Modal>

      {/* Modal de editar empleado */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={closeEditModal}
        shouldCloseOnOverlayClick={!isSavingEdit}
        className="bg-surface border border-line rounded-2xl shadow-card mx-4 max-w-md w-full p-0 overflow-hidden relative outline-none"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <h2 className="si-h2">Editar empleado</h2>
          <button onClick={closeEditModal} disabled={isSavingEdit} className="text-ink-3 hover:text-ink p-1 transition disabled:opacity-40" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>
        <form onSubmit={(e) => e.preventDefault()} className="p-6 space-y-4">
          <ModalField type="email" label="Email" name="email" value={editEmployee?.email || ''} onChange={handleEditChange} required={false} />
          <ModalField label="Teléfono" name="telefono" value={editEmployee?.telefono || ''} onChange={handleEditChange} required={false} />
          <ModalField
            type="text"
            inputMode="decimal"
            label="Puntos (reparto por puntos)"
            name="puntos"
            value={editEmployee?.puntos ?? 1}
            onChange={(e) => setEditEmployee({ ...editEmployee, puntos: limpiarNumero(e.target.value) })}
            required={false}
            hint="Cuánto vale en el reparto por puntos: 1 un mozo, 0,5 medio punto."
          />

          <div className="pt-2 border-t border-line">
            <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">Datos bancarios</div>
            <div className="grid grid-cols-2 gap-3">
              <ModalSelect label="Banco" name="banco" value={editEmployee?.banco || ''} onChange={handleEditChange} options={BANCOS_UY} required={false} />
              <ModalSelect label="Tipo de cuenta" name="tipoCuenta" value={editEmployee?.tipoCuenta || ''} onChange={handleEditChange} options={TIPOS_CUENTA} required={false} />
            </div>
            <div className="mt-3">
              <ModalField label="Número de cuenta" name="nCuenta" value={editEmployee?.nCuenta || ''} onChange={handleEditChange} required={false} />
            </div>
          </div>

          <button onClick={handleSaveEdit} disabled={isSavingEdit} className="si-btn-primary w-full mt-2">
            {isSavingEdit && <Spinner size={14} className="mr-2" />}
            {isSavingEdit ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>
      </Modal>

      {/* Modal de editar propina del día */}
      <Modal
        isOpen={isEditTipModalOpen}
        onRequestClose={closeEditTipModal}
        shouldCloseOnOverlayClick={!isUpdatingTip}
        className="bg-surface border border-line rounded-2xl shadow-card mx-4 max-w-md w-full p-0 overflow-hidden relative outline-none"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <h2 className="si-h2">Editar propina del día</h2>
          <button onClick={closeEditTipModal} disabled={isUpdatingTip} className="text-ink-3 hover:text-ink p-1 transition disabled:opacity-40" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="si-label">Nueva propina total</label>
            <input
              type="number"
              value={newTipAmount}
              onChange={(e) => setNewTipAmount(e.target.value)}
              className="si-input"
              disabled={isUpdatingTip}
            />
          </div>
          <button onClick={handleUpdateTip} disabled={isUpdatingTip} className="si-btn-primary w-full">
            {isUpdatingTip && <Spinner size={14} className="mr-2" />}
            {isUpdatingTip ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </Modal>

      {/* Confirmación de liquidación de saldo a un mozo dado de baja */}
      <ConfirmDialog
        isOpen={confirmLiquidar.open}
        onClose={() => setConfirmLiquidar({ open: false, employee: null })}
        onConfirm={performLiquidar}
        title="Liquidar saldo"
        message={
          confirmLiquidar.employee
            ? `Vas a registrar que le pagaste ${money(confirmLiquidar.employee.propina)} a ${confirmLiquidar.employee.Nombre} ${confirmLiquidar.employee.Apellido}. Su saldo queda en cero y el pago aparece en el historial y en el reporte del período. Hacelo después de haberle transferido la plata.`
            : ''
        }
        confirmLabel="Sí, ya le pagué"
        cancelLabel="Cancelar"
        loading={isLiquidando}
      />

      {/* Confirm dialog para eliminar empleado */}
      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, employee: null })}
        onConfirm={performDeleteEmployee}
        title="Desactivar mozo"
        message={
          confirmDelete.employee
            ? `¿Estás seguro que querés desactivar a ${confirmDelete.employee.Nombre} ${confirmDelete.employee.Apellido}? Podrás reactivarlo más adelante.` +
              // Avisar del saldo ANTES es lo que evita que la plata quede
              // encerrada: una vez de baja, el mozo no puede pedir el retiro
              // porque tampoco puede entrar a la app.
              ((confirmDelete.employee.propina || 0) > 0
                ? ` OJO: tiene ${money(confirmDelete.employee.propina)} sin retirar. Mientras esté de baja no va a poder pedirlos; vas a tener que liquidárselos desde la lista de mozos.`
                : '')
            : ''
        }
        confirmLabel="Sí, desactivar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={isDeletingEmployee}
      />
    </div>
  );
}

function ModalField({ label, name, value, onChange, type = 'text', required = true, step, min, hint, inputMode }) {
  return (
    <div>
      <label className="si-label" htmlFor={name}>{label}</label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="si-input"
        required={required}
        step={step}
        min={min}
        inputMode={inputMode}
      />
      {hint && <p className="text-xs text-ink-3 mt-1">{hint}</p>}
    </div>
  );
}

// Dropdown select reutilizable para el modal — mismo estilo visual que ModalField.
function ModalSelect({ label, name, value, onChange, options, required = false, placeholder = 'Seleccionar...' }) {
  return (
    <div>
      <label className="si-label" htmlFor={name}>{label}</label>
      <select
        id={name}
        name={name}
        value={value || ''}
        onChange={onChange}
        className="si-input"
        required={required}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

// Constantes compartidas para los selects bancarios.
// Prex y MiDinero no son bancos sino emisores de dinero electrónico, pero para
// el mozo son igual de válidos como destino del cobro, así que van en la misma
// lista. El backend no valida contra esta lista: `banco` es un varchar libre.
export const BANCOS_UY = [
  'BROU', 'Itaú', 'Santander', 'Scotia', 'BBVA', 'HSBC', 'Heritage', 'BANDES', 'Citibank',
  'Prex', 'MiDinero',
];
export const TIPOS_CUENTA = ['Caja de Ahorro', 'Cuenta Corriente'];

export default AdminPage;
